import { Component, OnInit, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as echarts from 'echarts';

@Component({
  selector: 'app-reports',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.scss'
})
export class ReportsComponent implements OnInit, AfterViewInit {
  // 圖表實例
  private monthlyChangesChart: any;
  private regulationTypeChart: any;
  private departmentChart: any;

  // DOM 元素引用
  @ViewChild('monthlyChangesChart') monthlyChangesChartRef!: ElementRef;
  @ViewChild('regulationTypeChart') regulationTypeChartRef!: ElementRef;
  @ViewChild('departmentChart') departmentChartRef!: ElementRef;

  // 總罰鍰風險統計
  totalPenaltyRisk: number = 0;
  mitigatedPenaltyRisk: number = 0;
  potentialPenaltyRisk: number = 0;
  riskMitigationPercentage: number = 0;

  constructor() {}

  ngOnInit(): void {
    // 載入報表數據
    this.loadReportData();
  }

  ngAfterViewInit(): void {
    // 初始化圖表
    this.initCharts();

    // 監聽視窗大小變化，調整圖表大小
    window.addEventListener('resize', () => {
      this.resizeCharts();
    });
  }

  // 載入報表數據
  loadReportData(): void {
    // 在這裡從服務或API獲取數據
    // 目前使用模擬數據進行示範
    this.loadMockData();
  }

  // 載入模擬數據
  loadMockData(): void {
    // 1. 總罰鍰風險
    this.totalPenaltyRisk = 5250000; // 總罰鍰風險 525萬
    this.mitigatedPenaltyRisk = 3150000; // 已避免的罰鍰風險 315萬
    this.potentialPenaltyRisk = this.totalPenaltyRisk - this.mitigatedPenaltyRisk; // 潛在風險
    this.riskMitigationPercentage = Math.round((this.mitigatedPenaltyRisk / this.totalPenaltyRisk) * 100);
  }

  // 初始化圖表
  initCharts(): void {
    this.initMonthlyChangesChart();
    this.initRegulationTypeChart();
    this.initDepartmentChart();
  }

  // 初始化每月法規異動圖表
  initMonthlyChangesChart(): void {
    if (!this.monthlyChangesChartRef) return;

    const chartElement = this.monthlyChangesChartRef.nativeElement;
    this.monthlyChangesChart = echarts.init(chartElement);

    const months = ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月'];
    const addedRegs = [5, 3, 7, 2, 6, 4, 8, 5, 3, 7, 9, 4];
    const modifiedRegs = [8, 12, 9, 14, 7, 10, 12, 9, 11, 13, 8, 10];
    const repealedRegs = [2, 1, 3, 1, 2, 1, 0, 2, 1, 3, 2, 1];

    const option = {
      title: {
        text: '每月法規異動狀況',
        left: 'center',
        textStyle: {
          color: '#484949'
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      legend: {
        data: ['新增', '修改', '廢除'],
        top: '30px'
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: months
      },
      yAxis: {
        type: 'value'
      },
      series: [
        {
          name: '新增',
          type: 'bar',
          stack: 'total',
          emphasis: {
            focus: 'series'
          },
          data: addedRegs,
          itemStyle: {
            color: '#4CAF50'
          }
        },
        {
          name: '修改',
          type: 'bar',
          stack: 'total',
          emphasis: {
            focus: 'series'
          },
          data: modifiedRegs,
          itemStyle: {
            color: '#2196F3'
          }
        },
        {
          name: '廢除',
          type: 'bar',
          stack: 'total',
          emphasis: {
            focus: 'series'
          },
          data: repealedRegs,
          itemStyle: {
            color: '#f44336'
          }
        }
      ]
    };

    this.monthlyChangesChart.setOption(option);
  }

  // 初始化法規類型分佈圖表
  initRegulationTypeChart(): void {
    if (!this.regulationTypeChartRef) return;

    const chartElement = this.regulationTypeChartRef.nativeElement;
    this.regulationTypeChart = echarts.init(chartElement);

    const option = {
      title: {
        text: '法規類型分佈',
        left: 'center',
        textStyle: {
          color: '#484949'
        }
      },
      tooltip: {
        trigger: 'item',
        formatter: '{a} <br/>{b}: {c} ({d}%)'
      },
      legend: {
        orient: 'vertical',
        left: 'left',
        top: 'middle',
        data: ['環保法規', '勞安法規', '消防法規', '職業衛生', '其他法規']
      },
      series: [
        {
          name: '法規類型',
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: true,
            formatter: '{b}: {c} ({d}%)'
          },
          emphasis: {
            label: {
              show: true,
              fontSize: '14',
              fontWeight: 'bold'
            }
          },
          labelLine: {
            show: true
          },
          data: [
            { value: 42, name: '環保法規', itemStyle: { color: '#4CAF50' } },
            { value: 28, name: '勞安法規', itemStyle: { color: '#2196F3' } },
            { value: 15, name: '消防法規', itemStyle: { color: '#f44336' } },
            { value: 22, name: '職業衛生', itemStyle: { color: '#FF9800' } },
            { value: 13, name: '其他法規', itemStyle: { color: '#9C27B0' } }
          ]
        }
      ]
    };

    this.regulationTypeChart.setOption(option);
  }

  // 初始化部門責任分佈圖表
  initDepartmentChart(): void {
    if (!this.departmentChartRef) return;

    const chartElement = this.departmentChartRef.nativeElement;
    this.departmentChart = echarts.init(chartElement);

    const option = {
      title: {
        text: '部門責任分佈',
        left: 'center',
        textStyle: {
          color: '#484949'
        }
      },
      tooltip: {
        trigger: 'item',
        formatter: '{a} <br/>{b}: {c} ({d}%)'
      },
      legend: {
        orient: 'vertical',
        left: 'left',
        top: 'middle',
        data: ['環保部', '安全部', '人資部', '工程部', '生產部', '品保部']
      },
      series: [
        {
          name: '負責法規數',
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: true,
            formatter: '{b}: {c} ({d}%)'
          },
          emphasis: {
            label: {
              show: true,
              fontSize: '14',
              fontWeight: 'bold'
            }
          },
          labelLine: {
            show: true
          },
          data: [
            { value: 35, name: '環保部', itemStyle: { color: '#4CAF50' } },
            { value: 25, name: '安全部', itemStyle: { color: '#2196F3' } },
            { value: 15, name: '人資部', itemStyle: { color: '#FF9800' } },
            { value: 12, name: '工程部', itemStyle: { color: '#f44336' } },
            { value: 8, name: '生產部', itemStyle: { color: '#9C27B0' } },
            { value: 5, name: '品保部', itemStyle: { color: '#795548' } }
          ]
        }
      ]
    };

    this.departmentChart.setOption(option);
  }

  // 調整圖表大小
  resizeCharts(): void {
    if (this.monthlyChangesChart) {
      this.monthlyChangesChart.resize();
    }
    if (this.regulationTypeChart) {
      this.regulationTypeChart.resize();
    }
    if (this.departmentChart) {
      this.departmentChart.resize();
    }
  }

  // 格式化金額顯示
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      maximumFractionDigits: 0
    }).format(amount);
  }
}
